import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import styles from './Signup.module.css';
import BackgroundPattern from '../../components/Landing/BackgroundPattern';
import arrow from '../../assets/pic/arrow.svg';
import { signup, login, sendEmailVerification } from '../../apis/api';

import Loading from '../../components/Loading/Loading';
import Snow from '../../components/Landing/Snow';

const Signup = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        passwordConfirm: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [isValid, setIsValid] = useState(false);

    const validateForm = (data) => {
        const { username, password, passwordConfirm } = data;
        const isUsernameValid = username.length >= 4;
        const isPasswordValid = password.length >= 4;
        const isConfirmValid = password === passwordConfirm && passwordConfirm.length > 0;
        return isUsernameValid && isPasswordValid && isConfirmValid;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            setIsValid(validateForm(newData));
            return newData;
        });
    };

    const handleSubmit = async () => {
        if (!isValid) return;

        setIsLoading(true);
        try {
            await signup(formData.username, formData.password);

            // 회원가입 직후: 토큰을 확보해야 인증 메일 발송 API를 호출할 수 있음
            try {
                const loginResponse = await login(formData.username, formData.password);
                const token = loginResponse.headers?.['authorization'] || loginResponse.headers?.['Authorization'];
                if (token) {
                    try {
                        const normalized = String(token).startsWith('Bearer ') ? String(token) : `Bearer ${token}`;
                        await sendEmailVerification(normalized);
                    } catch (e) {
                        // 이미 인증됨(400) 등은 UX를 막지 않음
                        console.warn('sendEmailVerification failed:', e);
                    }
                }
            } catch (e) {
                console.warn('Auto login for email verification failed:', e);
            }

            // Mark onboarding tour as pending for newly signed-up users
            localStorage.setItem('pendingOnboardingTour', 'true');
            localStorage.setItem('pendingOnboardingTourUsername', formData.username);
            alert('회원가입이 완료되었습니다! 인증 메일을 발송했습니다. 이메일을 확인한 뒤 로그인해주세요.');
            navigate('/login');
        } catch (error) {
            console.error('Signup error:', error);
            if (error.response && error.response.data) {
                const message = error.response.data.message || error.response.data;
                alert(`회원가입 실패: ${typeof message === 'string' ? message : '입력 정보를 확인해주세요.'}`);
            } else {
                alert('회원가입에 실패했습니다.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.isComposing || e.nativeEvent.isComposing) return;
        if (e.key === 'Enter' && isValid) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className={styles.container}>
            {isLoading && <Loading />}
            <BackgroundPattern />

            <Snow />

            <h2 className={styles.title}>회원가입</h2>

            <div className={styles.formContainer}>
                <div className={styles.inputGroup}>
                    <input
                        type="text"
                        name="username"
                        placeholder="아이디"
                        className={`${styles.inputField} ${formData.username ? styles.filled : ''}`}
                        value={formData.username}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="비밀번호"
                            className={`${styles.inputField} ${formData.password ? styles.filled : ''}`}
                            value={formData.password}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                        <button
                            className={styles.eyeButton}
                            onClick={() => setShowPassword(!showPassword)}
                            type="button"
                        >
                            {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                        </button>
                    </div>
                </div>

                <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="passwordConfirm"
                            placeholder="비밀번호 확인"
                            className={`${styles.inputField} ${formData.passwordConfirm ? styles.filled : ''}`}
                            value={formData.passwordConfirm}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                        <button
                            className={styles.eyeButton}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            type="button"
                        >
                            {showConfirmPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                        </button>
                    </div>

                </div>

                <button
                    className={`${styles.submitButton} ${isValid ? styles.active : ''}`}
                    onClick={handleSubmit}
                    disabled={!isValid}
                >
                    회원가입
                    <img src={arrow} alt="" className={styles.arrowIcon} />
                </button>
            </div>
        </div>
    );
};

export default Signup;
